import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { fetchTeamBoardAnalytics } from "../../../api/client";
import { ensureNamespaceLoadedSync } from "../../../i18n/lazyNamespaces";
import { NO_CLIENT_ID, UNASSIGNED_ASSIGNEE_ID } from "../utils/teamBoardCardFilters";
import { TaskAnalyticsPanels } from "./TaskAnalyticsPanels";

const fieldSx = { minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "1 1 180px" } };

const BoardAnalyticsDialog = ({
  open,
  onClose,
  boardId,
  boardName,
  assigneeOptions = [],
  clientOptions = [],
  columns = [],
}) => {
  ensureNamespaceLoadedSync("settings");
  const { t } = useTranslation("settings");
  const { t: tBoard } = useTranslation("teamTasks");
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs().endOf("month"));
  const [userId, setUserId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [columnId, setColumnId] = useState("");
  const [required, setRequired] = useState("");

  const startStr = start?.isValid?.() ? start.format("YYYY-MM-DD") : "";
  const endStr = end?.isValid?.() ? end.format("YYYY-MM-DD") : "";
  const periodOk = Boolean(startStr && endStr && startStr <= endStr);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["teamBoardAnalytics", boardId, startStr, endStr, userId, clientId, columnId, required],
    () =>
      fetchTeamBoardAnalytics(boardId, {
        start: startStr,
        end: endStr,
        userId: userId ?? undefined,
        clientId: clientId ?? undefined,
        columnId: columnId || undefined,
        required: required || undefined,
      }),
    { enabled: Boolean(open && boardId && periodOk), keepPreviousData: true }
  );

  const teamTasks = data?.teamTasks || null;
  const users = useMemo(
    () => [{ id: UNASSIGNED_ASSIGNEE_ID, nome: tBoard("inboxFilterUnassigned") }, ...assigneeOptions],
    [assigneeOptions, tBoard]
  );
  const clients = useMemo(
    () => [{ id: NO_CLIENT_ID, name: tBoard("boardFilterNoClient") }, ...clientOptions],
    [clientOptions, tBoard]
  );
  const selectedUser = users.find((u) => Number(u.id) === Number(userId)) || null;
  const selectedClient = clients.find((c) => Number(c.id) === Number(clientId)) || null;
  const errMsg =
    (typeof error?.message === "string" && error.message) ||
    (error ? tBoard("boardAnalyticsLoadError") : "");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>{tBoard("boardAnalyticsTitle")}</Typography>
        {boardName ? (
          <Typography variant="body2" color="text.secondary">{boardName}</Typography>
        ) : null}
        <IconButton aria-label={tBoard("closeCard")} onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {tBoard("boardAnalyticsHint")}
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
              <DatePicker
                label={t("userOperatorAnalytics.start")}
                value={start}
                onChange={(v) => v && setStart(v.startOf("day"))}
                slotProps={{ textField: { size: "small", sx: fieldSx } }}
              />
              <DatePicker
                label={t("userOperatorAnalytics.end")}
                value={end}
                onChange={(v) => v && setEnd(v.endOf("day"))}
                slotProps={{ textField: { size: "small", sx: fieldSx } }}
              />
              <Autocomplete
                size="small"
                options={users}
                value={selectedUser}
                onChange={(_, value) => setUserId(value ? Number(value.id) : null)}
                getOptionLabel={(option) => option?.nome || option?.name || ""}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                sx={fieldSx}
                renderInput={(params) => (
                  <TextField {...params} label={tBoard("boardAnalyticsColAssignee")} placeholder={tBoard("boardAnalyticsAllUsers")} />
                )}
              />
              <Autocomplete
                size="small"
                options={clients}
                value={selectedClient}
                onChange={(_, value) => setClientId(value ? Number(value.id) : null)}
                getOptionLabel={(option) => option?.name || option?.nome || ""}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                sx={fieldSx}
                renderInput={(params) => (
                  <TextField {...params} label={tBoard("boardFilterClient")} placeholder={tBoard("boardAnalyticsAllClients")} />
                )}
              />
              <TextField
                select
                size="small"
                label={tBoard("boardAnalyticsAllColumns")}
                value={columnId}
                onChange={(e) => setColumnId(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">{tBoard("boardAnalyticsAllColumns")}</MenuItem>
                {columns.map((col) => (
                  <MenuItem key={col.id} value={String(col.id)}>{col.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={tBoard("boardAnalyticsAllUrgency")}
                value={required}
                onChange={(e) => setRequired(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">{tBoard("boardAnalyticsAllUrgency")}</MenuItem>
                <MenuItem value="1">{tBoard("boardAnalyticsRequiredOnly")}</MenuItem>
                <MenuItem value="0">{tBoard("boardAnalyticsNormalOnly")}</MenuItem>
              </TextField>
              <Button variant="outlined" size="small" onClick={() => refetch()} disabled={!periodOk || isFetching} sx={{ alignSelf: "center" }}>
                {isFetching ? <CircularProgress size={16} /> : t("userOperatorAnalytics.apply")}
              </Button>
            </Stack>

            {errMsg ? <Alert severity="error">{errMsg}</Alert> : null}
            {isFetching ? <LinearProgress /> : null}
            <TaskAnalyticsPanels teamTasks={teamTasks} loading={isLoading && !teamTasks} variant="board" />
          </Stack>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  );
};

export default BoardAnalyticsDialog;
